import React, { useCallback, useState } from 'react';
import { getArrayMsg, stringify } from '@lib/utils';
import useErrorHandling from '../useErrorHandling';
import { FormContainer } from 'react-hook-form-mui';
import Grid from '@mui/material/Unstable_Grid2';
import Alerts from '../../Alerts';

/**
 * ModalForm serves as a boilerplate form wrapper for use in useFormModal. It houses the necessities for error handling.
 * While ModalForm does not create the react hook form object, it accepts it as a required prop. This way, form components
 * can be added from a more specific use case that may not have as many shared features as our basic form. One important
 * feature of this component is the submitRef which is supplied to a hidden button within the form that can be used
 * to submit the form externally.
 *
 * @param form - object created by useForm
 * @param onSubmit - submit function supplied to useForm's submit handler for received successful form data
 * @param submitRef - ref used for the hidden submit button to allow the external triggering of the form
 * @param children - fields of the form
 * @param formContext - alert functions to use when the alert messages need to appear outside the Modal form
 * @returns {JSX.Element}
 * @constructor
 */
const ModalForm = ({ form, onSubmit, submitRef, children, formContext = null }) => {
  const [wasSubmitting, setWasSubmitting] = useState(false);
  const [prevErrors, setPrevErrors] = useState({});
  const [pageMessages, setMessages] = useState(null);
  const [pageErrorMessages, setErrorMessages] = useState(null);

  const modalContext = {
    setPageMessage: useCallback((msg) => setMessages(getArrayMsg(msg)), []),
    setPageErrorMessage: useCallback((msg) => setErrorMessages(getArrayMsg(msg)), []),
    pageMessages,
    pageErrorMessages,
  };

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;

  useErrorHandling({
    errors,
    prevErrors,
    isSubmitting,
    wasSubmitting,
    setWasSubmitting,
    modalContext: formContext ?? modalContext,
    scrolling: false,
  });

  if (stringify(errors, false) !== stringify(prevErrors, false)) {
    setPrevErrors(errors);
  }

  return (
    <FormContainer formContext={form} onSuccess={handleSubmit(onSubmit)} FormProps={{ className: 'mui' }}>
      {!formContext &&
        <Grid container spacing={1} sx={{ marginBottom: '8px' }}>
          <Grid xs={12} id="modal-alerts">
            <Alerts modalContext={modalContext} />
          </Grid>
        </Grid>
      }
      {children}
      <button type="submit" style={{ display: 'none' }} ref={submitRef} />
    </FormContainer>
  );
};

export default ModalForm;
